/* Extraído de index.jsx (refatoração 4.5) — inputs, totais e painel de análise.
   Código movido VERBATIM, sem mudança de lógica. */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { dietAIService, dietPlansService, patientsService, authService, foodDatabaseService, settingsService } from '../../services/api';
import useIsMobile from '../../hooks/useIsMobile';
import { useToast } from '../../contexts/ToastContext';
import { SERVICES, STRATEGIES, sum, fmt1, dayTotals, guessCategory, getSubstitutes, serviceById, uniqueServices, strategyById, MEAL_ORDER, MEAL_ICONS, empty } from './helpers';
import { S, TotalsBar, MacroBlock, MacroPill, ServiceBadge, StrategyBadge, DataChip, Spinner } from './ui';
import { Ico, emo } from '../ui/Ico';
/* ════════════════════════════════════════════════════════
   TAB: PLANO MANUAL
════════════════════════════════════════════════════════ */
export const UNIDADES = ['g', 'ml', 'unidade', 'colher', 'xícara', 'porção'];

export const emptyAlimento = () => ({
  nome: '', quantidade: '100', unidade: 'g',
  calorias: '', proteina: '', carboidratos: '', gordura: '', fibra: '',
  _per100g: null,
});
export const emptyRefeicao = () => ({ nome: '', horario: '', alimentos: [emptyAlimento()] });

/* ── Modelos prontos ── */
export const MANUAL_TEMPLATES = [
  {
    id: 'emagrecimento_1200', label: 'Emagrecimento 1200 kcal', icon: '🔥',
    color: '#c46a60', bg: '#f8ebe9',
    refeicoes: [
      { nome: 'Café da Manhã', horario: '07:00', alimentos: [
        { nome: 'Ovo cozido', quantidade: '2', unidade: 'unidade', calorias: '140', proteina: '12', carboidratos: '2', gordura: '10', _per100g: null },
        { nome: 'Pão integral', quantidade: '1', unidade: 'unidade', calorias: '60', proteina: '3', carboidratos: '12', gordura: '1', _per100g: null },
        { nome: 'Café sem açúcar', quantidade: '200', unidade: 'ml', calorias: '5', proteina: '0', carboidratos: '1', gordura: '0', _per100g: null },
      ]},
      { nome: 'Lanche da Manhã', horario: '10:00', alimentos: [
        { nome: 'Iogurte natural desnatado', quantidade: '150', unidade: 'g', calorias: '75', proteina: '8', carboidratos: '9', gordura: '0', _per100g: null },
        { nome: 'Morango', quantidade: '100', unidade: 'g', calorias: '32', proteina: '1', carboidratos: '8', gordura: '0', _per100g: null },
      ]},
      { nome: 'Almoço', horario: '12:30', alimentos: [
        { nome: 'Arroz integral cozido', quantidade: '3', unidade: 'colher', calorias: '130', proteina: '3', carboidratos: '28', gordura: '1', _per100g: null },
        { nome: 'Feijão cozido', quantidade: '3', unidade: 'colher', calorias: '90', proteina: '6', carboidratos: '16', gordura: '0', _per100g: null },
        { nome: 'Frango grelhado', quantidade: '120', unidade: 'g', calorias: '170', proteina: '32', carboidratos: '0', gordura: '4', _per100g: null },
        { nome: 'Salada mista', quantidade: '100', unidade: 'g', calorias: '20', proteina: '1', carboidratos: '4', gordura: '0', _per100g: null },
      ]},
      { nome: 'Lanche da Tarde', horario: '15:30', alimentos: [
        { nome: 'Maçã', quantidade: '1', unidade: 'unidade', calorias: '80', proteina: '0', carboidratos: '21', gordura: '0', _per100g: null },
        { nome: 'Castanha-do-pará', quantidade: '2', unidade: 'unidade', calorias: '60', proteina: '1', carboidratos: '1', gordura: '6', _per100g: null },
      ]},
      { nome: 'Jantar', horario: '19:00', alimentos: [
        { nome: 'Sopa de legumes com frango', quantidade: '300', unidade: 'ml', calorias: '200', proteina: '18', carboidratos: '20', gordura: '4', _per100g: null },
      ]},
    ],
  },
  {
    id: 'emagrecimento_1500', label: 'Emagrecimento 1500 kcal', icon: '💪',
    color: '#D4A574', bg: '#fbf8f5',
    refeicoes: [
      { nome: 'Café da Manhã', horario: '07:00', alimentos: [
        { nome: 'Aveia em flocos', quantidade: '40', unidade: 'g', calorias: '150', proteina: '5', carboidratos: '27', gordura: '3', _per100g: null },
        { nome: 'Leite desnatado', quantidade: '200', unidade: 'ml', calorias: '68', proteina: '7', carboidratos: '9', gordura: '0', _per100g: null },
        { nome: 'Banana', quantidade: '1', unidade: 'unidade', calorias: '90', proteina: '1', carboidratos: '23', gordura: '0', _per100g: null },
      ]},
      { nome: 'Lanche da Manhã', horario: '10:00', alimentos: [
        { nome: 'Iogurte grego', quantidade: '170', unidade: 'g', calorias: '145', proteina: '17', carboidratos: '6', gordura: '4', _per100g: null },
        { nome: 'Granola sem açúcar', quantidade: '20', unidade: 'g', calorias: '80', proteina: '2', carboidratos: '15', gordura: '2', _per100g: null },
      ]},
      { nome: 'Almoço', horario: '12:30', alimentos: [
        { nome: 'Arroz integral cozido', quantidade: '4', unidade: 'colher', calorias: '170', proteina: '4', carboidratos: '37', gordura: '1', _per100g: null },
        { nome: 'Feijão cozido', quantidade: '3', unidade: 'colher', calorias: '90', proteina: '6', carboidratos: '16', gordura: '0', _per100g: null },
        { nome: 'Peixe grelhado (tilápia)', quantidade: '150', unidade: 'g', calorias: '160', proteina: '33', carboidratos: '0', gordura: '3', _per100g: null },
        { nome: 'Legumes refogados', quantidade: '150', unidade: 'g', calorias: '70', proteina: '2', carboidratos: '12', gordura: '2', _per100g: null },
      ]},
      { nome: 'Lanche da Tarde', horario: '15:30', alimentos: [
        { nome: 'Pão integral com ricota', quantidade: '1', unidade: 'unidade', calorias: '120', proteina: '7', carboidratos: '14', gordura: '4', _per100g: null },
        { nome: 'Suco de laranja natural', quantidade: '150', unidade: 'ml', calorias: '55', proteina: '1', carboidratos: '13', gordura: '0', _per100g: null },
      ]},
      { nome: 'Jantar', horario: '19:00', alimentos: [
        { nome: 'Peito de frango grelhado', quantidade: '120', unidade: 'g', calorias: '165', proteina: '31', carboidratos: '0', gordura: '3', _per100g: null },
        { nome: 'Batata-doce cozida', quantidade: '150', unidade: 'g', calorias: '130', proteina: '2', carboidratos: '31', gordura: '0', _per100g: null },
        { nome: 'Brócolis no vapor', quantidade: '100', unidade: 'g', calorias: '35', proteina: '3', carboidratos: '6', gordura: '0', _per100g: null },
      ]},
    ],
  },
  {
    id: 'low_carb_1400', label: 'Low Carb 1400 kcal', icon: '🥑',
    color: '#46714f', bg: '#edf4ee',
    refeicoes: [
      { nome: 'Café da Manhã', horario: '07:30', alimentos: [
        { nome: 'Ovo mexido', quantidade: '3', unidade: 'unidade', calorias: '210', proteina: '18', carboidratos: '3', gordura: '15', _per100g: null },
        { nome: 'Abacate', quantidade: '50', unidade: 'g', calorias: '80', proteina: '1', carboidratos: '3', gordura: '7', _per100g: null },
        { nome: 'Café sem açúcar', quantidade: '200', unidade: 'ml', calorias: '5', proteina: '0', carboidratos: '1', gordura: '0', _per100g: null },
      ]},
      { nome: 'Lanche da Manhã', horario: '10:30', alimentos: [
        { nome: 'Castanha-do-pará', quantidade: '30', unidade: 'g', calorias: '196', proteina: '4', carboidratos: '4', gordura: '20', _per100g: null },
        { nome: 'Queijo minas frescal', quantidade: '40', unidade: 'g', calorias: '75', proteina: '7', carboidratos: '1', gordura: '5', _per100g: null },
      ]},
      { nome: 'Almoço', horario: '12:30', alimentos: [
        { nome: 'Carne bovina grelhada', quantidade: '150', unidade: 'g', calorias: '250', proteina: '32', carboidratos: '0', gordura: '13', _per100g: null },
        { nome: 'Salada verde com azeite', quantidade: '150', unidade: 'g', calorias: '80', proteina: '2', carboidratos: '5', gordura: '6', _per100g: null },
        { nome: 'Abobrinha refogada', quantidade: '150', unidade: 'g', calorias: '50', proteina: '2', carboidratos: '6', gordura: '2', _per100g: null },
      ]},
      { nome: 'Lanche da Tarde', horario: '16:00', alimentos: [
        { nome: 'Iogurte grego integral', quantidade: '170', unidade: 'g', calorias: '190', proteina: '17', carboidratos: '8', gordura: '9', _per100g: null },
      ]},
      { nome: 'Jantar', horario: '19:30', alimentos: [
        { nome: 'Salmão grelhado', quantidade: '150', unidade: 'g', calorias: '270', proteina: '34', carboidratos: '0', gordura: '14', _per100g: null },
        { nome: 'Aspargos no vapor', quantidade: '100', unidade: 'g', calorias: '22', proteina: '2', carboidratos: '4', gordura: '0', _per100g: null },
        { nome: 'Azeite de oliva', quantidade: '1', unidade: 'colher', calorias: '90', proteina: '0', carboidratos: '0', gordura: '10', _per100g: null },
      ]},
    ],
  },
  {
    id: 'equilibrio_1800', label: 'Equilíbrio 1800 kcal', icon: '⚖️',
    color: '#4f6a86', bg: '#edf1f6',
    refeicoes: [
      { nome: 'Café da Manhã', horario: '07:00', alimentos: [
        { nome: 'Pão integral', quantidade: '2', unidade: 'unidade', calorias: '120', proteina: '6', carboidratos: '24', gordura: '2', _per100g: null },
        { nome: 'Ovo cozido', quantidade: '2', unidade: 'unidade', calorias: '140', proteina: '12', carboidratos: '2', gordura: '10', _per100g: null },
        { nome: 'Queijo branco', quantidade: '30', unidade: 'g', calorias: '60', proteina: '6', carboidratos: '1', gordura: '4', _per100g: null },
        { nome: 'Suco de laranja', quantidade: '200', unidade: 'ml', calorias: '75', proteina: '1', carboidratos: '18', gordura: '0', _per100g: null },
      ]},
      { nome: 'Lanche da Manhã', horario: '10:00', alimentos: [
        { nome: 'Fruta da estação', quantidade: '1', unidade: 'unidade', calorias: '80', proteina: '1', carboidratos: '20', gordura: '0', _per100g: null },
        { nome: 'Amêndoas', quantidade: '20', unidade: 'g', calorias: '115', proteina: '4', carboidratos: '3', gordura: '10', _per100g: null },
      ]},
      { nome: 'Almoço', horario: '12:00', alimentos: [
        { nome: 'Arroz branco cozido', quantidade: '5', unidade: 'colher', calorias: '210', proteina: '4', carboidratos: '46', gordura: '0', _per100g: null },
        { nome: 'Feijão cozido', quantidade: '4', unidade: 'colher', calorias: '120', proteina: '8', carboidratos: '21', gordura: '0', _per100g: null },
        { nome: 'Frango grelhado', quantidade: '150', unidade: 'g', calorias: '210', proteina: '40', carboidratos: '0', gordura: '5', _per100g: null },
        { nome: 'Legumes cozidos', quantidade: '150', unidade: 'g', calorias: '70', proteina: '3', carboidratos: '12', gordura: '1', _per100g: null },
        { nome: 'Salada com azeite', quantidade: '100', unidade: 'g', calorias: '60', proteina: '1', carboidratos: '4', gordura: '5', _per100g: null },
      ]},
      { nome: 'Lanche da Tarde', horario: '15:30', alimentos: [
        { nome: 'Tapioca', quantidade: '1', unidade: 'unidade', calorias: '90', proteina: '1', carboidratos: '23', gordura: '0', _per100g: null },
        { nome: 'Frango desfiado', quantidade: '60', unidade: 'g', calorias: '85', proteina: '18', carboidratos: '0', gordura: '2', _per100g: null },
      ]},
      { nome: 'Jantar', horario: '19:00', alimentos: [
        { nome: 'Macarrão integral', quantidade: '80', unidade: 'g', calorias: '130', proteina: '5', carboidratos: '27', gordura: '1', _per100g: null },
        { nome: 'Molho de tomate caseiro', quantidade: '100', unidade: 'g', calorias: '40', proteina: '2', carboidratos: '8', gordura: '1', _per100g: null },
        { nome: 'Carne moída refogada', quantidade: '100', unidade: 'g', calorias: '180', proteina: '22', carboidratos: '0', gordura: '10', _per100g: null },
      ]},
      { nome: 'Ceia', horario: '21:30', alimentos: [
        { nome: 'Leite morno', quantidade: '200', unidade: 'ml', calorias: '68', proteina: '7', carboidratos: '9', gordura: '0', _per100g: null },
      ]},
    ],
  },
];

/* ── Busca de alimento com autocomplete ── */
export function FoodSearchInput({ value, onChange, onSelect, placeholder }) {
  const [query,   setQuery]   = useState(value || '');
  const [results, setResults] = useState([]);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const debRef  = React.useRef(null);
  const wrapRef = React.useRef(null);

  // sync external value changes (e.g. template load)
  React.useEffect(() => { setQuery(value || ''); }, [value]);

  React.useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    onChange(q); // keep parent text in sync
    clearTimeout(debRef.current);
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await foodDatabaseService.search(q);
        if (res.success) {
          setResults(res.data?.slice(0, 8) || []);
          setOpen(true);
        }
      } catch { /* silent */ }
      finally { setLoading(false); }
    }, 320);
  };

  const handleSelect = (food) => {
    setQuery(food.nome);
    setOpen(false);
    onSelect(food);
  };

  const macroPill = (label, val, color) => (
    <span style={{ background: color + '18', color, fontSize: '0.7rem', fontWeight: 600, padding: '1px 6px', borderRadius: '10px', whiteSpace: 'nowrap' }}>
      {emo(label)} {Math.round(val)}
    </span>
  );

  return (
    <div ref={wrapRef} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <input
        style={{ ...S.cellIn, width: '100%', minWidth: '140px' }}
        value={query}
        onChange={handleChange}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder={placeholder || 'Buscar alimento...'}
      />
      {loading && (
        <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.7rem', color: '#a39b93' }}>...</span>
      )}
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 3px)', left: 0, right: 0, zIndex: 500,
          background: '#ffffff', border: '1.5px solid #d9d0c6', borderRadius: '8px',
          boxShadow: '0 6px 20px rgba(0,0,0,0.12)', maxHeight: '280px', overflowY: 'auto',
        }}>
          {results.map((food) => (
            <div
              key={food.id}
              onMouseDown={() => handleSelect(food)}
              style={{ padding: '0.55rem 0.85rem', cursor: 'pointer', borderBottom: '1px solid #f6f0ea', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#fbf8f5'; }}
              onMouseLeave={e => { e.currentTarget.style.background = ''; }}
            >
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#2f2a26', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {emo(food.nome)}
              </span>
              <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
                {emo(macroPill('🔥', food.calorias_100g, '#D4A574'))}
                {macroPill('P', food.proteina_100g, '#7189a6')}
                {macroPill('C', food.carbo_100g, '#b9d2bd')}
                {macroPill('G', food.gordura_100g, '#e8c9a8')}
              </div>
            </div>
          ))}
          <div style={{ padding: '0.35rem 0.85rem', fontSize: '0.7rem', color: '#a39b93', borderTop: '1px solid #ebe3da' }}>
            por 100g · selecione para preencher automaticamente
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Totais por refeição ── */
export function MealTotals({ alimentos }) {
  const cal  = alimentos.reduce((s, a) => s + (parseFloat(a.calorias)     || 0), 0);
  const prot = alimentos.reduce((s, a) => s + (parseFloat(a.proteina)     || 0), 0);
  const carb = alimentos.reduce((s, a) => s + (parseFloat(a.carboidratos) || 0), 0);
  const fat  = alimentos.reduce((s, a) => s + (parseFloat(a.gordura)      || 0), 0);
  if (cal === 0 && prot === 0 && carb === 0 && fat === 0) return null;
  const pill = (label, val, color) => (
    <span style={{ background: color + '18', color, fontSize: '0.75rem', fontWeight: 600, padding: '2px 9px', borderRadius: '20px' }}>
      {emo(label)} {parseFloat(val.toFixed(1))}
    </span>
  );
  return (
    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.6rem', paddingTop: '0.6rem', borderTop: '1px dashed #ebe3da', alignItems: 'center' }}>
      <span style={{ fontSize: '0.7rem', color: '#a39b93', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>Total:</span>
      {emo(pill('🔥', cal, '#D4A574'))}
      {pill('P', prot, '#7189a6')}
      {pill('C', carb, '#b9d2bd')}
      {pill('G', fat, '#e8c9a8')}
      <span style={{ fontSize: '0.7rem', color: '#d9d0c6' }}>kcal · g</span>
    </div>
  );
}

/* ── Barra de totais diários ── */
export function DailyTotalsBar({ refeicoes }) {
  const all  = refeicoes.flatMap(r => r.alimentos);
  const cal  = all.reduce((s, a) => s + (parseFloat(a.calorias)     || 0), 0);
  const prot = all.reduce((s, a) => s + (parseFloat(a.proteina)     || 0), 0);
  const carb = all.reduce((s, a) => s + (parseFloat(a.carboidratos) || 0), 0);
  const fat  = all.reduce((s, a) => s + (parseFloat(a.gordura)      || 0), 0);
  if (cal === 0) return null;

  const calProt = prot * 4;
  const calCarb = carb * 4;
  const calFat  = fat  * 9;
  const calMacro = calProt + calCarb + calFat || 1;
  const pProt = Math.round(calProt / calMacro * 100);
  const pCarb = Math.round(calCarb / calMacro * 100);
  const pFat  = 100 - pProt - pCarb;

  return (
    <div style={{ background: '#ffffff', border: '1.5px solid #D4A574', borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <span style={{ fontSize: '2rem', fontWeight: 700, color: '#D4A574' }}>{Math.round(cal)}</span>
        <span style={{ fontSize: '0.9rem', color: '#736b64' }}>kcal totais do dia</span>
      </div>
      <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        {emo([
          { icon: '💪', label: 'Proteína',   val: prot.toFixed(1) + 'g', pct: pProt, color: '#7189a6' },
          { icon: '🌾', label: 'Carboidr.',  val: carb.toFixed(1) + 'g', pct: pCarb, color: '#b9d2bd' },
          { icon: '🥑', label: 'Gordura',    val: fat.toFixed(1)  + 'g', pct: pFat,  color: '#e8c9a8' },
        ].map(({ icon, label, val, pct, color }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span>{emo(icon)}</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#2f2a26' }}>{val} <span style={{ fontWeight: 500, color: '#736b64', fontSize: '0.8rem' }}>({pct}%)</span></div>
              <div style={{ fontSize: '0.72rem', color: '#736b64' }}>{emo(label)}</div>
            </div>
          </div>
        )))}
      </div>
      <div style={{ height: '8px', borderRadius: '4px', overflow: 'hidden', background: '#ebe3da', display: 'flex' }}>
        <div style={{ width: `${pProt}%`, background: '#7189a6', height: '100%' }} />
        <div style={{ width: `${pCarb}%`, background: '#b9d2bd', height: '100%' }} />
        <div style={{ width: `${pFat}%`,  background: '#e8c9a8', height: '100%' }} />
      </div>
    </div>
  );
}

/* ── Modal de modelos prontos ── */
export function TemplatesModal({ onSelect, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
      onClick={onClose}>
      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '1.5rem', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#2f2a26' }}><Ico e="📋" sp />Usar Modelo</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#736b64', cursor: 'pointer', fontSize: '1rem', padding: '2px 6px' }}><Ico e="✕" /></button>
        </div>
        <p style={{ margin: '0 0 1rem', fontSize: '0.83rem', color: '#736b64' }}>
          Selecione um modelo para pré-preencher as refeições. Você poderá editar tudo após aplicar.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {MANUAL_TEMPLATES.map(t => (
            <button
              key={t.id}
              onClick={() => onSelect(t)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.85rem',
                padding: '0.85rem 1rem', borderRadius: '10px', cursor: 'pointer',
                border: `1.5px solid ${t.color}40`, background: t.bg, textAlign: 'left', width: '100%',
              }}
            >
              <span style={{ fontSize: '1.6rem', flexShrink: 0 }}>{emo(t.icon)}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: t.color, fontSize: '0.92rem' }}>{emo(t.label)}</div>
                <div style={{ fontSize: '0.78rem', color: '#736b64', marginTop: '2px' }}>
                  {t.refeicoes.length} refeições · {Math.round(t.refeicoes.flatMap(r => r.alimentos).reduce((s, a) => s + (parseFloat(a.calorias) || 0), 0))} kcal
                </div>
              </div>
              <span style={{ color: t.color, fontSize: '1rem' }}>→</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Tabela de micronutrientes por 100g ─────────────────────────────
   [ferro_mg, calcio_mg, vitC_mg, vitD_mcg, vitB12_mcg, magnesio_mg, potassio_mg, zinco_mg]
   Chave: substring do nome do alimento (lowercase)                   */
export const MICRO_TABLE = {
  'frango':      [0.9,  11,  0,    0.3, 0.4, 23,  248, 1.5],
  'carne bovi':  [2.6,  8,   0,    0,   2.0, 17,  310, 4.2],
  'patinho':     [2.6,  8,   0,    0,   2.0, 17,  310, 4.2],
  'alcatra':     [2.2,  7,   0,    0,   1.8, 16,  290, 4.0],
  'salmão':      [0.5,  15,  0,    16.5,3.2, 27,  363, 0.6],
  'atum':        [1.3,  16,  0,    5.8, 2.5, 31,  441, 0.7],
  'tilápia':     [0.4,  14,  0,    4.0, 1.6, 32,  380, 0.5],
  'sardinha':    [1.9,  382, 0,    4.8, 8.9, 39,  397, 1.3],
  'bacalhau':    [1.0,  60,  0,    3.3, 1.0, 32,  400, 0.6],
  'ovo':         [1.8,  50,  0,    2.0, 0.9, 10,  126, 1.1],
  'leite inte':  [0.05, 123, 0,    1.0, 0.9, 12,  150, 0.4],
  'leite desn':  [0.05, 125, 0,    1.0, 0.9, 11,  156, 0.4],
  'leite':       [0.05, 123, 0,    1.0, 0.9, 12,  150, 0.4],
  'iogurte gre': [0.1,  100, 0,    0,   0.4, 11,  141, 0.5],
  'iogurte':     [0.1,  121, 0.5,  0,   0.4, 12,  155, 0.6],
  'queijo':      [0.3,  320, 0,    0.2, 0.8, 20,  95,  1.4],
  'ricota':      [0.3,  155, 0,    0,   0.5, 13,  105, 0.5],
  'feijão':      [2.8,  40,  0,    0,   0,   36,  355, 1.0],
  'lentilha':    [3.3,  36,  2.1,  0,   0,   36,  369, 1.3],
  'grão-de-bic': [2.9,  49,  1.3,  0,   0,   48,  291, 1.5],
  'soja':        [3.6,  145, 1.7,  0,   0,   58,  396, 1.0],
  'tofu':        [2.4,  350, 0,    0,   0,   58,  121, 1.0],
  'edamame':     [2.3,  60,  6.1,  0,   0,   61,  446, 1.4],
  'arroz':       [0.3,  5,   0,    0,   0,   12,  35,  0.5],
  'batata-doce': [0.6,  30,  2.4,  0,   0,   25,  337, 0.3],
  'batata ing':  [0.4,  5,   7.4,  0,   0,   20,  379, 0.3],
  'mandioca':    [0.5,  15,  19.3, 0,   0,   21,  271, 0.3],
  'aveia':       [4.7,  54,  0,    0,   0,   177, 429, 3.6],
  'pão integ':   [2.1,  76,  0,    0,   0,   42,  206, 1.8],
  'pão':         [1.5,  40,  0,    0,   0,   18,  120, 0.8],
  'macarrão':    [1.2,  12,  0,    0,   0,   25,  44,  1.0],
  'tapioca':     [0.1,  3,   0,    0,   0,   6,   11,  0.0],
  'brócolis':    [0.7,  47,  89,   0,   0,   21,  316, 0.4],
  'espinafre':   [2.7,  99,  28,   0,   0,   79,  558, 0.5],
  'cenoura':     [0.3,  33,  5.9,  0,   0,   12,  320, 0.2],
  'tomate':      [0.3,  10,  13.7, 0,   0,   11,  237, 0.2],
  'abobrinha':   [0.4,  17,  17.9, 0,   0,   18,  262, 0.3],
  'couve':       [1.0,  245, 120,  0,   0,   22,  215, 0.3],
  'alface':      [0.4,  33,  9.2,  0,   0,   12,  195, 0.2],
  'banana':      [0.3,  5,   8.7,  0,   0,   27,  358, 0.2],
  'maçã':        [0.1,  6,   4.6,  0,   0,   5,   107, 0.0],
  'laranja':     [0.1,  40,  53,   0,   0,   10,  181, 0.1],
  'mamão':       [0.1,  20,  61.8, 0,   0,   21,  182, 0.1],
  'manga':       [0.1,  10,  36.4, 0,   0,   10,  168, 0.1],
  'abacate':     [0.6,  12,  10,   0,   0,   29,  485, 0.6],
  'morango':     [0.4,  16,  58.8, 0,   0,   13,  153, 0.1],
  'uva':         [0.4,  11,  4,    0,   0,   7,   191, 0.1],
  'azeite':      [0.6,  1,   0,    0,   0,   0,   1,   0  ],
  'castanha-do': [2.4,  160, 0.7,  0,   0,   376, 659, 4.1],
  'castanha':    [2.0,  130, 0.5,  0,   0,   200, 500, 2.5],
  'amêndoa':     [3.7,  269, 0,    0,   0,   270, 733, 3.1],
  'amendoim':    [1.6,  55,  0,    0,   0,   177, 704, 2.9],
  'chia':        [7.7,  631, 1.6,  0,   0,   335, 407, 4.6],
  'linhaça':     [5.7,  255, 0.6,  0,   0,   392, 813, 4.3],
};

export const DDR_MICROS = { ferro: 18, calcio: 1000, vitC: 75, vitD: 15, vitB12: 2.4, magnesio: 320, potassio: 4700, zinco: 8 };

export function getMicrosFromAlimento(al) {
  const lower = (al.nome || '').toLowerCase();
  const qtd   = (al.unidade === 'g' || al.unidade === 'ml') ? (parseFloat(al.quantidade) || 0) : 0;
  if (!qtd) return null;
  for (const [key, vals] of Object.entries(MICRO_TABLE)) {
    if (lower.includes(key)) {
      const f = qtd / 100;
      return { ferro: vals[0]*f, calcio: vals[1]*f, vitC: vals[2]*f, vitD: vals[3]*f, vitB12: vals[4]*f, magnesio: vals[5]*f, potassio: vals[6]*f, zinco: vals[7]*f };
    }
  }
  return null;
}

/* ── Donut SVG ────────────────────────────────────────────────────── */
export function SvgDonut({ data, size = 84, stroke = 13, centerLabel = null }) {
  const r    = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const total = data.reduce((s, d) => s + (d.value || 0), 0) || 1;
  let cumLen = 0;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ display: 'block', transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#ebe3da" strokeWidth={stroke} />
        {data.map((d, i) => {
          if (!d.value) return null;
          const len    = (d.value / total) * circ;
          const dashOff = -cumLen;
          cumLen += len;
          return (
            <circle key={i} cx={size/2} cy={size/2} r={r}
              fill="none" stroke={d.color} strokeWidth={stroke}
              strokeDasharray={`${len} ${circ - len}`}
              strokeDashoffset={dashOff} strokeLinecap="butt" />
          );
        })}
      </svg>
      {centerLabel && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {centerLabel}
        </div>
      )}
    </div>
  );
}

/* ── Painel lateral de análise ────────────────────────────────────── */
export function AnalysisPanel({ refeicoes, patientId, metaKcal: metaKcalProp }) {
  const [patientGoals, setPatientGoals] = useState(null);
  const [metaKcal,     setMetaKcal]     = useState(metaKcalProp || 2000);

  useEffect(() => {
    if (!patientId) return;
    patientsService.getById(patientId).then(res => {
      if (!res?.success) return;
      const p = res.data;
      const peso   = parseFloat(p.initial_weight || p.last_weight) || 0;
      const altura = parseFloat(p.initial_height) || 0;
      const nasc   = p.birth_date;
      const sexo   = (p.sex || p.gender || 'f').toLowerCase();
      if (peso && altura && nasc) {
        const idade = new Date().getFullYear() - new Date(nasc).getFullYear();
        const h     = altura > 3 ? altura : altura * 100;
        // Mifflin-St Jeor
        const tmb = sexo === 'm'
          ? 10 * peso + 6.25 * h - 5 * idade + 5
          : 10 * peso + 6.25 * h - 5 * idade - 161;
        const ger = Math.round(tmb * 1.4);
        setMetaKcal(ger);
        setPatientGoals({ peso, altura: h, idade, sexo, ger });
      }
    }).catch(() => {});
  }, [patientId]);

  // Totais do dia
  const allAl   = refeicoes.flatMap(r => r.alimentos);
  const totCal  = allAl.reduce((s, a) => s + (parseFloat(a.calorias)     || 0), 0);
  const totProt = allAl.reduce((s, a) => s + (parseFloat(a.proteina)     || 0), 0);
  const totCarb = allAl.reduce((s, a) => s + (parseFloat(a.carboidratos) || 0), 0);
  const totFat  = allAl.reduce((s, a) => s + (parseFloat(a.gordura)      || 0), 0);
  const totFib  = allAl.reduce((s, a) => s + (parseFloat(a.fibra)        || 0), 0);

  // Metas de macros derivadas da meta calórica
  const metaProt  = Math.round(metaKcal * 0.22 / 4);
  const metaCarb  = Math.round(metaKcal * 0.53 / 4);
  const metaFat   = Math.round(metaKcal * 0.25 / 9);
  const metaFib   = 25;

  // Micronutrientes do dia
  const zeroMicro = { ferro: 0, calcio: 0, vitC: 0, vitD: 0, vitB12: 0, magnesio: 0, potassio: 0, zinco: 0 };
  const micros = allAl.reduce((acc, al) => {
    const m = getMicrosFromAlimento(al);
    if (!m) return acc;
    return Object.fromEntries(Object.keys(acc).map(k => [k, acc[k] + (m[k] || 0)]));
  }, { ...zeroMicro });

  // Dados para donut principal
  const donutData = [
    { value: totProt * 4, color: '#7189a6' },
    { value: totCarb * 4, color: '#D4A574' },
    { value: totFat  * 9, color: '#e8c9a8' },
  ];

  const pct     = (val, meta) => Math.min(Math.round((val / (meta || 1)) * 100), 100);
  const fmt     = (n) => parseFloat((n || 0).toFixed(1));
  const pctCal  = pct(totCal, metaKcal);

  const MacroBar = ({ label, val, meta, color, unit = 'g' }) => {
    const p = pct(val, meta);
    return (
      <div style={{ marginBottom: '0.7rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#4a4440' }}>{emo(label)}</span>
          <span style={{ fontSize: '0.75rem', color: '#736b64' }}>{fmt(val)}{unit} / {meta}{unit}</span>
        </div>
        <div style={{ height: '5px', background: '#ebe3da', borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${p}%`, background: color, borderRadius: '3px', transition: 'width 0.3s' }} />
        </div>
      </div>
    );
  };

  const MicroRow = ({ label, k, unit, ddr }) => {
    const val = micros[k] || 0;
    const p   = pct(val, ddr);
    const color = p >= 100 ? '#46714f' : p >= 60 ? '#D4A574' : '#c46a60';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0', borderBottom: '1px solid #f6f0ea' }}>
        <span style={{ fontSize: '0.75rem', color: '#4a4440', width: '90px', flexShrink: 0, fontWeight: 500 }}>{emo(label)}</span>
        <div style={{ flex: 1, height: '4px', background: '#ebe3da', borderRadius: '2px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${p}%`, background: color, transition: 'width 0.3s' }} />
        </div>
        <span style={{ fontSize: '0.7rem', color: '#736b64', width: '36px', textAlign: 'right', flexShrink: 0 }}>{p}%</span>
      </div>
    );
  };

  const panelStyle = {
    background: '#ffffff',
    border: '1.5px solid #ebe3da',
    borderRadius: '14px',
    padding: '1rem',
    position: 'sticky',
    top: '1rem',
    maxHeight: 'calc(100vh - 2rem)',
    overflowY: 'auto',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  };

  const sectionTitle = (title) => (
    <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#a39b93', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem', marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid #f6f0ea' }}>
      {emo(title)}
    </div>
  );

  return (
    <div style={panelStyle}>
      {/* SEÇÃO 1 — Valor energético */}
      <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#a39b93', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
        Valor energético
      </div>

      {/* Kcal + donut centralizados */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', marginBottom: '0.85rem' }}>
        <SvgDonut
          data={donutData} size={110} stroke={16}
          centerLabel={
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#2f2a26', lineHeight: 1 }}>{Math.round(totCal)}</div>
              <div style={{ fontSize: '0.65rem', color: '#736b64', marginTop: '2px' }}>kcal</div>
            </div>
          }
        />

        {/* Barra de progresso kcal vs meta */}
        <div style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
            <span style={{ fontSize: '0.72rem', color: '#736b64' }}>Meta: {metaKcal} kcal</span>
            <span style={{ fontSize: '0.72rem', fontWeight: 600, color: pctCal >= 100 ? '#46714f' : '#D4A574' }}>{pctCal}%</span>
          </div>
          <div style={{ height: '6px', background: '#ebe3da', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pctCal}%`, background: pctCal >= 100 ? '#46714f' : '#D4A574', borderRadius: '4px', transition: 'width 0.3s' }} />
          </div>
        </div>

        {/* Legenda do donut */}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          {[
            { label: 'Prot.', color: '#7189a6' },
            { label: 'Carb.', color: '#D4A574' },
            { label: 'Gord.', color: '#e8c9a8' },
          ].map(({ label, color }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
              <span style={{ fontSize: '0.7rem', color: '#736b64' }}>{emo(label)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* SEÇÃO 2 — Macros detalhados */}
      {sectionTitle('Macros detalhados')}
      <MacroBar label="Proteína"      val={totProt} meta={metaProt} color="#7189a6" />
      <MacroBar label="Carboidratos"  val={totCarb} meta={metaCarb} color="#D4A574" />
      <MacroBar label="Gordura"       val={totFat}  meta={metaFat}  color="#e8c9a8" />
      <MacroBar label="Fibra"         val={totFib}  meta={metaFib}  color="#b9d2bd" />

      {/* SEÇÃO 3 — Refeições */}
      {sectionTitle('Refeições')}
      <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.25rem' }}>
        {refeicoes.map((ref, ri) => {
          const rCal  = ref.alimentos.reduce((s, a) => s + (parseFloat(a.calorias)     || 0), 0);
          const rProt = ref.alimentos.reduce((s, a) => s + (parseFloat(a.proteina)     || 0), 0);
          const rCarb = ref.alimentos.reduce((s, a) => s + (parseFloat(a.carboidratos) || 0), 0);
          const rFat  = ref.alimentos.reduce((s, a) => s + (parseFloat(a.gordura)      || 0), 0);
          const mDat  = [
            { value: rProt * 4, color: '#7189a6' },
            { value: rCarb * 4, color: '#D4A574' },
            { value: rFat  * 9, color: '#e8c9a8' },
          ];
          return (
            <button key={ri}
              onClick={() => { const el = document.getElementById(`refCard-${ri}`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', background: 'none', border: '1.5px solid transparent', borderRadius: '10px', padding: '0.4rem', cursor: 'pointer', minWidth: '56px', transition: 'border-color 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#ebe3da'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; }}
            >
              <SvgDonut data={mDat} size={44} stroke={8}
                centerLabel={
                  rCal > 0 ? <span style={{ fontSize: '0.55rem', fontWeight: 600, color: '#D4A574' }}>{Math.round(rCal)}</span> : null
                }
              />
              <span style={{ fontSize: '0.62rem', color: '#736b64', textAlign: 'center', lineHeight: 1.2, maxWidth: '54px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {emo(ref.nome || `Ref. ${ri + 1}`)}
              </span>
            </button>
          );
        })}
      </div>

      {/* SEÇÃO 4 — Micronutrientes */}
      {sectionTitle('Micronutrientes')}
      <div style={{ fontSize: '0.67rem', color: '#d9d0c6', marginBottom: '0.5rem', lineHeight: 1.4 }}>
        Estimativa baseada nos alimentos inseridos (% da DDR)
      </div>
      {[
        { label: 'Ferro',        k: 'ferro',    ddr: DDR_MICROS.ferro    },
        { label: 'Cálcio',       k: 'calcio',   ddr: DDR_MICROS.calcio   },
        { label: 'Vitamina C',   k: 'vitC',     ddr: DDR_MICROS.vitC     },
        { label: 'Vitamina D',   k: 'vitD',     ddr: DDR_MICROS.vitD     },
        { label: 'Vitamina B12', k: 'vitB12',   ddr: DDR_MICROS.vitB12   },
        { label: 'Magnésio',     k: 'magnesio', ddr: DDR_MICROS.magnesio },
        { label: 'Potássio',     k: 'potassio', ddr: DDR_MICROS.potassio },
        { label: 'Zinco',        k: 'zinco',    ddr: DDR_MICROS.zinco    },
      ].map(({ label, k, ddr }) => (
        <MicroRow key={k} label={label} k={k} ddr={ddr} />
      ))}

      {/* Info de metas */}
      {patientGoals && (
        <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.6rem', background: '#fbf8f5', borderRadius: '8px', fontSize: '0.7rem', color: '#736b64', lineHeight: 1.5 }}>
          Meta calculada por Mifflin-St Jeor ({patientGoals.peso}kg · {patientGoals.altura}cm · {patientGoals.idade}a)
        </div>
      )}
    </div>
  );
}

